import { Dispatch, SetStateAction } from 'react'
import { FieldErrors } from 'react-hook-form'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { MenuItem, Tooltip } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import PermIdentityRoundedIcon from '@mui/icons-material/PermIdentityRounded'

import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen'
import { useGetPatientQuery } from '@/core/services/patient'
import { useAppSelector } from '@/redux/rootReducer'
import { selectDMPConnectTlsiConnectorId } from '@/redux/reducers/dmp-connect.reducer'

import { useSearchIns } from '../../model/useSearchIns'

type GetInsProps = {
  onClose?: () => void
  patientId: string
  setFormErrors: Dispatch<SetStateAction<FieldErrors<PatientProfileFormData>>>
}

const INS_CALL_BLOCKED_TOOLTIP = msg`The INSi call is not possible for an identity flagged as Doubtful or Fictitious`

export function GetIns({ onClose, patientId, setFormErrors }: GetInsProps) {
  const lingui = useLingui()
  const isLargeScreen = useIsLargeScreen()
  const { data: patient } = useGetPatientQuery(patientId)
  const { isINSEnabled, isInsCallBlocked } = useIsINSEnabled(patient)
  const { searchIns, isFetchingIns } = useSearchIns({ patientId, setFormErrors })

  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)

  if (!isINSEnabled || !tlsiConnectorId) return null

  if (isLargeScreen) {
    const button = (
      <LoadingButton disabled={isInsCallBlocked} loading={isFetchingIns} onClick={searchIns} startIcon={<PermIdentityRoundedIcon />} variant="contained">
        <Trans>Get INS</Trans>
      </LoadingButton>
    )
    return isInsCallBlocked ? (
      <Tooltip title={lingui._(INS_CALL_BLOCKED_TOOLTIP)}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    )
  }

  return (
    <Tooltip title={isInsCallBlocked ? lingui._(INS_CALL_BLOCKED_TOOLTIP) : ''}>
      <MenuItem
        disableRipple
        disabled={isInsCallBlocked}
        onClick={() => {
          if (isInsCallBlocked) return
          searchIns()
          onClose?.()
        }}
      >
        <PermIdentityRoundedIcon />
        <Trans>Get INS</Trans>
      </MenuItem>
    </Tooltip>
  )
}
